/**
 * @file thumbnailGenerator.ts
 * @description Client-side thumbnail generation utility for instant preview
 * Generates 320x320px thumbnails in browser using Canvas API
 *
 * Features:
 * - Instant preview (no server roundtrip)
 * - Supports images: JPEG, PNG, GIF, WebP, SVG
 * - Supports PDFs: Renders first page using PDF.js
 * - Memory efficient (releases resources)
 * - Error handling with fallback
 * - Quality optimization (70% for balance)
 *
 * @version 1.1.0
 */

// Lazy-loaded heavy libraries (loaded on first use only)
let pdfjsLib: typeof import('pdfjs-dist') | null = null;
let mammothModule: typeof import('mammoth') | null = null;

async function getPdfJs() {
  if (!pdfjsLib) {
    pdfjsLib = await import('pdfjs-dist');
    pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;
  }
  return pdfjsLib;
}

async function getMammoth() {
  if (!mammothModule) {
    mammothModule = await import('mammoth');
  }
  return mammothModule;
}

/**
 * Configuration for thumbnail generation
 */
export const THUMBNAIL_CONFIG = {
  width: 320,
  height: 320,
  quality: 0.7, // JPEG quality (0-1), 70% for good balance
  format: 'image/jpeg' as const,
  fit: 'cover' as const, // Options: 'cover', 'contain'

  // Supported image MIME types
  supportedImageTypes: [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/svg+xml',
    'image/bmp'
  ] as readonly string[],

  // Supported document types (PDF)
  supportedDocTypes: [
    'application/pdf'
  ] as readonly string[],

  // Supported text types (TXT, MD, DOCX, CSV)
  supportedTextTypes: [
    'text/plain',                                                              // .txt
    'text/markdown',                                                           // .md (some systems)
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'text/csv'                                                                 // .csv
  ] as readonly string[],

  // All supported types (for backward compatibility)
  get supportedTypes(): readonly string[] {
    return [...this.supportedImageTypes, ...this.supportedDocTypes, ...this.supportedTextTypes];
  }
};

/**
 * File with client-side generated thumbnail
 */
export interface FileWithThumbnail {
  file: File;
  thumbnailDataUrl: string | null;
  error?: string;
}

/**
 * Checks if a file type is a supported image type
 * @param mimeType - MIME type of the file
 * @returns True if supported image
 */
export function isSupportedImageType(mimeType: string): boolean {
  if (!mimeType) return false;
  return THUMBNAIL_CONFIG.supportedImageTypes.includes(mimeType.toLowerCase());
}

/**
 * Checks if a file type is a PDF
 * @param mimeType - MIME type of the file
 * @returns True if PDF
 */
export function isPdfType(mimeType: string): boolean {
  if (!mimeType) return false;
  return THUMBNAIL_CONFIG.supportedDocTypes.includes(mimeType.toLowerCase());
}

/**
 * Checks if a file type is a supported text type (TXT, MD, DOCX)
 * @param mimeType - MIME type of the file
 * @param fileName - Optional filename to check extension (for .md files that may have text/plain MIME)
 * @returns True if supported text type
 */
export function isTextType(mimeType: string, fileName?: string): boolean {
  if (!mimeType) return false;
  const lowerMime = mimeType.toLowerCase();

  // Check MIME type directly
  if (THUMBNAIL_CONFIG.supportedTextTypes.includes(lowerMime)) {
    return true;
  }

  // Special case: .md files often have text/plain MIME type
  if (fileName && lowerMime === 'text/plain') {
    const ext = fileName.toLowerCase().split('.').pop();
    return ext === 'md' || ext === 'markdown';
  }

  return false;
}

/**
 * Checks if a file is a DOCX document
 * @param mimeType - MIME type of the file
 * @returns True if DOCX
 */
export function isDocxType(mimeType: string): boolean {
  if (!mimeType) return false;
  return mimeType.toLowerCase() === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
}

/**
 * Checks if a file type is supported for thumbnail generation (images, PDFs, or text files)
 * @param mimeType - MIME type of the file
 * @param fileName - Optional filename to check extension (for .md files)
 * @returns True if supported
 */
export function isSupportedType(mimeType: string, fileName?: string): boolean {
  return isSupportedImageType(mimeType) || isPdfType(mimeType) || isTextType(mimeType, fileName);
}

/**
 * Generates a client-side thumbnail for an image, PDF, or text file
 *
 * @param file - File object to generate thumbnail from
 * @returns Promise resolving to data URL (base64) or null if unsupported/error
 *
 * @example
 * const thumbnail = await generateClientThumbnail(imageFile);
 * if (thumbnail) {
 *   <img src={thumbnail} alt="Preview" />
 * }
 */
export async function generateClientThumbnail(file: File): Promise<string | null> {
  // 🔍 THUMBNAIL DEBUG: Log entry point for every file
  console.log(`[ThumbnailGenerator] 🔍 DEBUG - generateClientThumbnail called:`, {
    name: file.name,
    type: file.type,
    size: file.size,
    isSupportedResult: isSupportedType(file.type, file.name),
    isTextResult: isTextType(file.type, file.name),
    isPdfResult: isPdfType(file.type),
    isDocxResult: isDocxType(file.type),
    isImageResult: isSupportedImageType(file.type)
  });

  // Validation: Check if file type is supported (pass filename for .md detection)
  if (!isSupportedType(file.type, file.name)) {
    console.log(`[ThumbnailGenerator] Unsupported type: ${file.type}, skipping thumbnail`);
    return null;
  }

  // Size check: Skip very large files (>50MB) to avoid memory issues
  const MAX_SIZE = 50 * 1024 * 1024; // 50MB
  if (file.size > MAX_SIZE) {
    console.warn(`[ThumbnailGenerator] File too large (${(file.size / 1024 / 1024).toFixed(1)}MB), skipping thumbnail`);
    return null;
  }

  const startTime = performance.now();

  try {
    let thumbnailDataUrl: string;

    // Handle PDFs
    if (isPdfType(file.type)) {
      thumbnailDataUrl = await generatePdfThumbnail(file);
    }
    // Handle text files (TXT, MD) - but NOT DOCX yet (requires mammoth)
    else if (isTextType(file.type, file.name) && !isDocxType(file.type)) {
      const text = await extractTextContent(file);
      thumbnailDataUrl = await generateTextPreviewThumbnail(file, text);
    }
    // Handle DOCX files using mammoth for text extraction
    else if (isDocxType(file.type)) {
      const text = await extractDocxText(file);
      thumbnailDataUrl = await generateTextPreviewThumbnail(file, text);
    }
    // Handle images
    else {
      // Step 1: Read file as Data URL
      const dataUrl = await readFileAsDataURL(file);

      // Step 2: Load image into HTMLImageElement
      const img = await loadImage(dataUrl);

      // Step 3: Resize image using Canvas
      thumbnailDataUrl = resizeImageToThumbnail(img, {
        width: THUMBNAIL_CONFIG.width,
        height: THUMBNAIL_CONFIG.height,
        quality: THUMBNAIL_CONFIG.quality,
        fit: THUMBNAIL_CONFIG.fit
      });
    }

    const endTime = performance.now();
    const duration = Math.round(endTime - startTime);

    console.log(`[ThumbnailGenerator] Generated thumbnail for ${file.name} in ${duration}ms`);

    return thumbnailDataUrl;

  } catch (error) {
    console.error(`[ThumbnailGenerator] Failed to generate thumbnail for ${file.name}:`, error);
    return null;
  }
}

/**
 * Generates thumbnails for multiple files in parallel
 *
 * @param files - Array of File objects
 * @returns Promise resolving to array of FileWithThumbnail objects
 */
export async function generateClientThumbnailsBatch(files: File[]): Promise<FileWithThumbnail[]> {
  console.log(`[ThumbnailGenerator] Batch processing ${files.length} files`);

  const startTime = performance.now();

  // Process all files in parallel
  const results = await Promise.all(
    files.map(async (file) => {
      try {
        const thumbnailDataUrl = await generateClientThumbnail(file);
        return {
          file,
          thumbnailDataUrl
        };
      } catch (error) {
        console.error(`[ThumbnailGenerator] Error processing ${file.name}:`, error);
        return {
          file,
          thumbnailDataUrl: null,
          error: error instanceof Error ? error.message : 'Unknown error'
        };
      }
    })
  );

  const endTime = performance.now();
  const duration = Math.round(endTime - startTime);
  const successCount = results.filter(r => r.thumbnailDataUrl !== null).length;

  // 🔍 THUMBNAIL DEBUG: Log batch results
  console.log(`[ThumbnailGenerator] 🔍 DEBUG - Batch results:`, results.map(r => ({
    fileName: r.file.name,
    fileType: r.file.type,
    fileSize: r.file.size,
    hasThumbnail: !!r.thumbnailDataUrl,
    thumbnailLength: r.thumbnailDataUrl?.length || 0,
    error: r.error || null
  })));

  console.log(`[ThumbnailGenerator] Batch complete: ${successCount}/${files.length} thumbnails generated in ${duration}ms`);

  return results;
}

// ============================================================
// Helper Functions
// ============================================================

/**
 * Generates a thumbnail from a PDF file using PDF.js
 * Renders the first page of the PDF to a canvas
 *
 * @param file - PDF file to generate thumbnail from
 * @returns Promise resolving to data URL of the thumbnail
 */
async function generatePdfThumbnail(file: File): Promise<string> {
  console.log(`[ThumbnailGenerator] Processing PDF: ${file.name}`);

  const pdfjs = await getPdfJs();

  // Read file as ArrayBuffer for PDF.js
  const arrayBuffer = await file.arrayBuffer();

  // Load PDF document
  const loadingTask = pdfjs.getDocument({ data: arrayBuffer });
  const pdf = await loadingTask.promise;

  console.log(`[ThumbnailGenerator] PDF loaded, pages: ${pdf.numPages}`);

  // Get first page
  const page = await pdf.getPage(1);

  // Calculate scale to fit thumbnail dimensions while maintaining aspect ratio
  const viewport = page.getViewport({ scale: 1.0 });
  const scale = Math.min(
    THUMBNAIL_CONFIG.width / viewport.width,
    THUMBNAIL_CONFIG.height / viewport.height
  );
  const scaledViewport = page.getViewport({ scale });

  // Create canvas for rendering
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Failed to get canvas 2d context for PDF rendering');
  }

  // Set canvas size to scaled viewport
  canvas.width = scaledViewport.width;
  canvas.height = scaledViewport.height;

  // Render PDF page to canvas
  await page.render({
    canvas: canvas,
    canvasContext: context,
    viewport: scaledViewport
  }).promise;

  // Now resize to exact thumbnail dimensions with cover/contain
  const thumbnailCanvas = document.createElement('canvas');
  const thumbnailCtx = thumbnailCanvas.getContext('2d');

  if (!thumbnailCtx) {
    throw new Error('Failed to get thumbnail canvas 2d context');
  }

  thumbnailCanvas.width = THUMBNAIL_CONFIG.width;
  thumbnailCanvas.height = THUMBNAIL_CONFIG.height;

  // Fill with white background (for PDFs with transparency)
  thumbnailCtx.fillStyle = '#FFFFFF';
  thumbnailCtx.fillRect(0, 0, THUMBNAIL_CONFIG.width, THUMBNAIL_CONFIG.height);

  // Calculate draw parameters for 'contain' fit (show full page)
  const sourceAspect = canvas.width / canvas.height;
  const targetAspect = THUMBNAIL_CONFIG.width / THUMBNAIL_CONFIG.height;

  let dx = 0, dy = 0, dWidth = THUMBNAIL_CONFIG.width, dHeight = THUMBNAIL_CONFIG.height;

  if (sourceAspect > targetAspect) {
    // PDF page is wider, add vertical padding
    dHeight = THUMBNAIL_CONFIG.width / sourceAspect;
    dy = (THUMBNAIL_CONFIG.height - dHeight) / 2;
  } else {
    // PDF page is taller, add horizontal padding
    dWidth = THUMBNAIL_CONFIG.height * sourceAspect;
    dx = (THUMBNAIL_CONFIG.width - dWidth) / 2;
  }

  // Draw rendered PDF page onto thumbnail canvas
  thumbnailCtx.drawImage(canvas, 0, 0, canvas.width, canvas.height, dx, dy, dWidth, dHeight);

  // Convert to data URL
  const thumbnailDataUrl = thumbnailCanvas.toDataURL(THUMBNAIL_CONFIG.format, THUMBNAIL_CONFIG.quality);

  // Cleanup
  canvas.width = 0;
  canvas.height = 0;
  thumbnailCanvas.width = 0;
  thumbnailCanvas.height = 0;

  // Cleanup PDF.js resources
  await pdf.destroy();

  console.log(`[ThumbnailGenerator] PDF thumbnail generated for ${file.name}`);

  return thumbnailDataUrl;
}

/**
 * Generates a thumbnail preview for text-based files (TXT, MD)
 * Renders a styled preview with filename header and text content
 *
 * @param file - File object to generate thumbnail from
 * @param text - Pre-extracted text content
 * @returns Promise resolving to data URL of the thumbnail
 */
async function generateTextPreviewThumbnail(file: File, text: string): Promise<string> {
  console.log(`[ThumbnailGenerator] Processing text file: ${file.name}`);

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get canvas 2d context for text rendering');
  }

  canvas.width = THUMBNAIL_CONFIG.width;
  canvas.height = THUMBNAIL_CONFIG.height;

  // Background - light gray for document feel
  ctx.fillStyle = '#F8F9FA';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Header bar
  const headerHeight = 44;
  ctx.fillStyle = '#E9ECEF';
  ctx.fillRect(0, 0, canvas.width, headerHeight);

  // Header bottom border
  ctx.strokeStyle = '#DEE2E6';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, headerHeight);
  ctx.lineTo(canvas.width, headerHeight);
  ctx.stroke();

  // File icon (text representation based on type)
  ctx.font = '22px system-ui, -apple-system, sans-serif';
  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  let icon = '📄';
  if (ext === 'md' || ext === 'markdown') {
    icon = '📝';
  } else if (ext === 'txt') {
    icon = '📃';
  } else if (ext === 'csv') {
    icon = '📊';
  }
  ctx.fillText(icon, 12, 30);

  // Filename (truncated if needed)
  ctx.fillStyle = '#495057';
  ctx.font = 'bold 15px system-ui, -apple-system, sans-serif';
  const maxNameLength = 28;
  const displayName = file.name.length > maxNameLength
    ? file.name.substring(0, maxNameLength - 3) + '...'
    : file.name;
  ctx.fillText(displayName, 44, 28);

  // Text content area
  ctx.fillStyle = '#212529';
  ctx.font = '13px "SF Mono", Monaco, Consolas, monospace';

  // Split text into lines and limit
  const lines = text.split('\n').slice(0, 12);
  const lineHeight = 22;
  const startY = headerHeight + 24;
  const maxCharsPerLine = 32;

  lines.forEach((line, i) => {
    // Truncate long lines
    const displayLine = line.length > maxCharsPerLine
      ? line.substring(0, maxCharsPerLine - 1) + '…'
      : line;
    // Replace tabs with spaces for consistent rendering
    const cleanLine = displayLine.replace(/\t/g, '  ');
    ctx.fillText(cleanLine, 12, startY + i * lineHeight);
  });

  // If there's more content, show indicator
  if (text.split('\n').length > 12) {
    ctx.fillStyle = '#6C757D';
    ctx.font = 'italic 12px system-ui, -apple-system, sans-serif';
    ctx.fillText('...', 12, canvas.height - 12);
  }

  // Border around entire thumbnail
  ctx.strokeStyle = '#DEE2E6';
  ctx.lineWidth = 2;
  ctx.strokeRect(1, 1, canvas.width - 2, canvas.height - 2);

  // Convert to data URL
  const thumbnailDataUrl = canvas.toDataURL(THUMBNAIL_CONFIG.format, THUMBNAIL_CONFIG.quality);

  // Cleanup
  canvas.width = 0;
  canvas.height = 0;

  console.log(`[ThumbnailGenerator] Text thumbnail generated for ${file.name}`);

  return thumbnailDataUrl;
}

/**
 * Extracts text content from a text file (TXT, MD)
 * Limits extraction to prevent memory issues with large files
 *
 * @param file - File object to extract text from
 * @returns Promise resolving to extracted text (limited to ~5KB)
 */
async function extractTextContent(file: File): Promise<string> {
  // Limit to first 5KB to prevent memory issues
  const MAX_TEXT_SIZE = 5 * 1024;
  const blob = file.slice(0, MAX_TEXT_SIZE);

  try {
    const text = await blob.text();
    return text;
  } catch (error) {
    console.error(`[ThumbnailGenerator] Failed to read text from ${file.name}:`, error);
    throw error;
  }
}

/**
 * Extracts text content from a DOCX file using mammoth
 *
 * @param file - DOCX file to extract text from
 * @returns Promise resolving to extracted text
 */
async function extractDocxText(file: File): Promise<string> {
  console.log(`[ThumbnailGenerator] Extracting text from DOCX: ${file.name}`);

  try {
    const mammoth = await getMammoth();
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });

    if (result.messages.length > 0) {
      console.log(`[ThumbnailGenerator] DOCX extraction messages:`, result.messages);
    }

    return result.value;
  } catch (error) {
    console.error(`[ThumbnailGenerator] Failed to extract DOCX text from ${file.name}:`, error);
    throw error;
  }
}

/**
 * Reads a File object as Data URL (base64)
 * @param file - File to read
 * @returns Promise resolving to data URL string
 */
function readFileAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      if (event.target?.result && typeof event.target.result === 'string') {
        resolve(event.target.result);
      } else {
        reject(new Error('Failed to read file as data URL'));
      }
    };

    reader.onerror = () => {
      reject(new Error(`FileReader error: ${reader.error?.message || 'Unknown error'}`));
    };

    reader.readAsDataURL(file);
  });
}

/**
 * Loads a data URL into an HTMLImageElement
 * @param dataUrl - Data URL string
 * @returns Promise resolving to loaded image
 */
function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      resolve(img);
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    // Start loading
    img.src = dataUrl;
  });
}

/**
 * Resize options for thumbnail generation
 */
interface ResizeOptions {
  width: number;
  height: number;
  quality: number;
  fit: 'cover' | 'contain';
}

/**
 * Resizes an image to thumbnail size using Canvas API
 * @param img - HTMLImageElement to resize
 * @param options - Resize options
 * @returns Data URL of the resized image
 */
function resizeImageToThumbnail(img: HTMLImageElement, options: ResizeOptions): string {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get canvas 2d context');
  }

  // Set canvas size to target dimensions
  canvas.width = options.width;
  canvas.height = options.height;

  // Calculate source and destination rectangles for crop/fit
  const { sx, sy, sWidth, sHeight, dx, dy, dWidth, dHeight } = calculateDrawParams(
    img.width,
    img.height,
    options.width,
    options.height,
    options.fit
  );

  // Enable image smoothing for better quality
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // Draw image on canvas with calculated parameters
  ctx.drawImage(img, sx, sy, sWidth, sHeight, dx, dy, dWidth, dHeight);

  // Convert canvas to data URL (JPEG with quality)
  const thumbnailDataUrl = canvas.toDataURL(THUMBNAIL_CONFIG.format, options.quality);

  // Clean up: Release canvas memory (important for mobile)
  canvas.width = 0;
  canvas.height = 0;

  return thumbnailDataUrl;
}

/**
 * Calculates draw parameters for canvas based on fit mode
 * @returns Object with source (s*) and destination (d*) parameters for drawImage
 */
function calculateDrawParams(
  sourceWidth: number,
  sourceHeight: number,
  targetWidth: number,
  targetHeight: number,
  fit: 'cover' | 'contain'
): {
  sx: number; sy: number; sWidth: number; sHeight: number;
  dx: number; dy: number; dWidth: number; dHeight: number;
} {
  const sourceAspect = sourceWidth / sourceHeight;
  const targetAspect = targetWidth / targetHeight;

  if (fit === 'cover') {
    // Cover: Fill entire canvas, crop if necessary
    if (sourceAspect > targetAspect) {
      // Source is wider, crop horizontally
      const newSourceWidth = sourceHeight * targetAspect;
      return {
        sx: (sourceWidth - newSourceWidth) / 2,
        sy: 0,
        sWidth: newSourceWidth,
        sHeight: sourceHeight,
        dx: 0,
        dy: 0,
        dWidth: targetWidth,
        dHeight: targetHeight
      };
    } else {
      // Source is taller, crop vertically
      const newSourceHeight = sourceWidth / targetAspect;
      return {
        sx: 0,
        sy: (sourceHeight - newSourceHeight) / 2,
        sWidth: sourceWidth,
        sHeight: newSourceHeight,
        dx: 0,
        dy: 0,
        dWidth: targetWidth,
        dHeight: targetHeight
      };
    }
  } else {
    // Contain: Fit entire image inside canvas, add padding if necessary
    if (sourceAspect > targetAspect) {
      // Source is wider, add vertical padding
      const newHeight = targetWidth / sourceAspect;
      return {
        sx: 0,
        sy: 0,
        sWidth: sourceWidth,
        sHeight: sourceHeight,
        dx: 0,
        dy: (targetHeight - newHeight) / 2,
        dWidth: targetWidth,
        dHeight: newHeight
      };
    } else {
      // Source is taller, add horizontal padding
      const newWidth = targetHeight * sourceAspect;
      return {
        sx: 0,
        sy: 0,
        sWidth: sourceWidth,
        sHeight: sourceHeight,
        dx: (targetWidth - newWidth) / 2,
        dy: 0,
        dWidth: newWidth,
        dHeight: targetHeight
      };
    }
  }
}

/**
 * Estimates the size of a data URL in bytes
 * @param dataUrl - Data URL string
 * @returns Estimated size in bytes
 */
export function estimateDataUrlSize(dataUrl: string): number {
  // Base64 encoding increases size by ~33%
  // data:image/jpeg;base64,... prefix
  const base64String = dataUrl.split(',')[1] || '';
  return Math.ceil((base64String.length * 3) / 4);
}

/**
 * Formats file size for display
 * @param bytes - Size in bytes
 * @returns Formatted string (e.g., "1.5 MB")
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}
